import React, { useState, useEffect, useMemo } from 'react';
import { FaEdit, FaTrash, FaPlus, FaKey, FaSearch, FaFilter, FaTimes, FaLayerGroup } from 'react-icons/fa';
import Swal from 'sweetalert2';
import DataTable from '../../components_shared/DataTable';
import { permissionAPI } from '../../utils/api';

const MODULE_COLORS = {
  USERS:       '#0f6fad',
  ROLES:       '#8e44ad',
  PERMISSIONS: '#e84393',
  PROCUREMENT: '#e67e22',
  INVENTORY:   '#10ac84',
  ENROLLMENT:  '#3498db',
  FINANCE:     '#27ae60',
  REPORTS:     '#16a085',
  GENERAL:     '#636e72',
};

function getModuleColor(mod) {
  return MODULE_COLORS[(mod || '').toUpperCase()] || MODULE_COLORS.GENERAL;
}

export default function PermissionsTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, perm: null });
  const [form, setForm] = useState({ name: '', description: '', module: '' });
  const [formErrors, setFormErrors] = useState({});

  // Filters
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');

  useEffect(() => { fetchPermissions(); }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const res = await permissionAPI.getPermissions();
      const perms = Array.isArray(res.data) ? res.data : (res.data.content || []);
      setData(perms);
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const modules = useMemo(() => {
    const s = new Set(data.map(p => (p.module || 'General').toUpperCase()));
    return Array.from(s).sort();
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter(p => {
      const matchSearch = !search || (
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.description?.toLowerCase().includes(search.toLowerCase())
      );
      const matchModule = !moduleFilter || (p.module || 'General').toUpperCase() === moduleFilter.toUpperCase();
      return matchSearch && matchModule;
    });
  }, [data, search, moduleFilter]);

  const openCreate = () => {
    setForm({ name: '', description: '', module: '' });
    setFormErrors({});
    setModal({ open: true, perm: null });
  };

  const openEdit = perm => {
    setForm({ name: perm.name, description: perm.description || '', module: perm.module || '' });
    setFormErrors({});
    setModal({ open: true, perm });
  };

  const validateForm = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'System name is required';
    else if (!/^[A-Z0-9_]+$/.test(form.name.trim())) e.name = 'Use UPPER_CASE format (e.g. VIEW_BOOKS)';
    if (!form.description.trim()) e.description = 'Description is required';
    setFormErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = { ...form, name: form.name.trim().toUpperCase() };
      if (modal.perm) {
        await permissionAPI.updatePermission(modal.perm.id, payload);
        Swal.fire({ title: 'Updated', text: 'Permission updated', icon: 'success', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
      } else {
        await permissionAPI.createPermission(payload);
        Swal.fire({ title: 'Created', text: 'Permission registered', icon: 'success', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
      }
      setModal({ open: false, perm: null });
      fetchPermissions();
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async perm => {
    const result = await Swal.fire({
      title: 'Delete Permission?',
      html: `Remove <code style="color:#e84393">${perm.name}</code>?<br/><span style="font-size:0.85rem;color:#94a3b8">Roles using this permission may lose access.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setLoading(true);
    try {
      await permissionAPI.deletePermission(perm.id);
      fetchPermissions();
      Swal.fire({ title: 'Deleted!', text: 'Permission removed.', icon: 'success', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'System Name',
      cell: ({ getValue }) => (
        <code style={{
          fontFamily: "'Courier New', monospace",
          fontSize: '0.82rem', fontWeight: 700,
          background: 'rgba(232,67,147,0.07)',
          color: '#e84393', padding: '3px 8px', borderRadius: 5,
        }}>
          {getValue()}
        </code>
      ),
    },
    { accessorKey: 'description', header: 'Description' },
    {
      accessorKey: 'module',
      header: 'Module',
      cell: ({ getValue }) => {
        const mod = (getValue() || 'General').toUpperCase();
        const color = getModuleColor(mod);
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: `${color}12`, color,
            borderRadius: 6, padding: '3px 10px',
            fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            <FaLayerGroup style={{ fontSize: '0.6rem' }} />
            {mod}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <ActionBtn icon={<FaEdit />} title="Edit" color="#0f6fad" onClick={() => openEdit(row.original)} />
          <ActionBtn icon={<FaTrash />} title="Delete" color="#e74c3c" onClick={() => handleDelete(row.original)} />
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Stats row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard value={data.length}   label="Total Permissions" color="#e84393" />
        <StatCard value={modules.length} label="Modules Covered"  color="#0f6fad" />
        <StatCard value={data.filter(p => !p.module).length} label="Uncategorized" color="#636e72" />
      </div>

      {/* Main card */}
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '22px 28px 0', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaKey style={{ color: '#e84393' }} /> Permission Registry
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>Atomic system capabilities and their definitions</p>
          </div>
          <button onClick={openCreate} style={primaryBtn}>
            <FaPlus /> Register Permission
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, padding: '16px 28px', borderBottom: '1px solid #f0f4f8', flexWrap: 'wrap' }}>
          <div style={searchBoxStyle}>
            <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem', marginRight: 8, flexShrink: 0 }} />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or description..."
              style={searchInputStyle}
            />
            {search && <ClearBtn onClick={() => setSearch('')} />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaFilter style={{ color: '#94a3b8', fontSize: '0.75rem' }} />
            <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} style={selectStyle}>
              <option value="">All Modules</option>
              {modules.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        <div style={{ padding: '0 28px 24px' }}>
          <DataTable data={filtered} columns={columns} loading={loading} />
        </div>
      </div>

      {/* Create / Edit modal */}
      {modal.open && (
        <ModalOverlay onClose={() => setModal({ open: false, perm: null })}>
          <ModalHeader
            title={modal.perm ? 'Edit Permission' : 'Register Permission'}
            onClose={() => setModal({ open: false, perm: null })}
          />
          <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px' }}>
            <Field label="System Name (UPPER_CASE)" error={formErrors.name}>
              <input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value.toUpperCase() })}
                placeholder="e.g. VIEW_BOOKS"
                style={{ ...fieldInputStyle, ...(formErrors.name ? errorBorder : {}) }}
                required
              />
            </Field>
            <Field label="Display Description" error={formErrors.description}>
              <input
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Describe what this permission allows..."
                style={{ ...fieldInputStyle, ...(formErrors.description ? errorBorder : {}) }}
                required
              />
            </Field>
            <Field label="Module Group">
              <input
                value={form.module}
                onChange={e => setForm({ ...form, module: e.target.value })}
                placeholder="e.g. PROCUREMENT, ENROLLMENT..."
                style={fieldInputStyle}
              />
            </Field>
            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button type="button" onClick={() => setModal({ open: false, perm: null })} style={ghostBtn}>Cancel</button>
              <button type="submit" disabled={loading} style={primaryBtn}>{loading ? 'Saving…' : 'Save Permission'}</button>
            </div>
          </form>
        </ModalOverlay>
      )}

    </div>
  );
}

/* ---- Shared UI helpers ---- */

function StatCard({ value, label, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #f0f4f8', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: '1.3rem', fontWeight: 700, flexShrink: 0 }}>
        {value}
      </div>
      <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 500 }}>{label}</div>
    </div>
  );
}

function ActionBtn({ icon, title, color, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button title={title} onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ width: 30, height: 30, borderRadius: 7, background: h ? `${color}18` : 'transparent', border: `1px solid ${h ? color : 'transparent'}`, color, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', transition: 'all 0.15s', padding: 0 }}>
      {icon}
    </button>
  );
}

function ModalOverlay({ children, onClose, maxWidth = 480 }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
      <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>{title}</h3>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.1rem', padding: 4 }}><FaTimes /></button>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</label>
      {children}
      {error && <span style={{ fontSize: '0.75rem', color: '#e74c3c', marginTop: 4, display: 'block' }}>{error}</span>}
    </div>
  );
}

function ClearBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <FaTimes style={{ fontSize: '0.75rem' }} />
    </button>
  );
}

/* ---- Style constants ---- */
const cardStyle = { background: '#fff', borderRadius: 14, border: '1px solid #f0f4f8', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' };
const primaryBtn = { display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, #10ac84, #0e9a75)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins',sans-serif", boxShadow: '0 2px 8px rgba(16,172,132,0.25)' };
const ghostBtn   = { flex: 1, padding: '10px', borderRadius: 10, background: '#f1f5f9', color: '#374151', border: 'none', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins',sans-serif" };
const searchBoxStyle = { flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '0 12px', height: 40 };
const searchInputStyle = { flex: 1, border: 'none', background: 'transparent', fontSize: '0.875rem', color: '#374151', outline: 'none', fontFamily: "'Poppins',sans-serif" };
const selectStyle = { padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', outline: 'none', fontSize: '0.875rem', background: '#f8fafc', color: '#374151', fontFamily: "'Poppins',sans-serif", cursor: 'pointer' };
const fieldInputStyle = { width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#f8fafc', fontSize: '0.875rem', color: '#374151', outline: 'none', fontFamily: "'Poppins',sans-serif", boxSizing: 'border-box' };
const errorBorder = { borderColor: '#e74c3c', background: 'rgba(231,76,60,0.03)' };
